import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    input,
    InputSignal,
    resource,
    ResourceRef,
    Signal,
    signal,
    viewChild,
    WritableSignal
} from "@angular/core";
import {StoreData} from "../store-search.model";
import {ImageCacheDirective} from '../../ui-elements/image/image-cache.directive';
import {NgClass} from '@angular/common';
import {Config} from '../../configuration/config';
import {ConfigurationService} from '@fedelta-pos/data-client';

/**
 * Store Map Panel component.
 */
@Component({
    selector: "woa-store-map-panel",
    templateUrl: "store-map-panel.component.html",
    styleUrl: "./store-map-panel.component.scss",
    imports: [
        ImageCacheDirective,
        NgClass
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class StoreMapPanelComponent {
    private configurationService: ConfigurationService = inject(ConfigurationService);

    public readonly store: InputSignal<StoreData> = input.required<StoreData>();

    public readonly circleMap: InputSignal<boolean> = input<boolean>(false);

    private readonly map: Signal<ElementRef> = viewChild.required("map");

    private readonly viewReady: WritableSignal<boolean> = signal(false);
    private readonly retryTrigger: WritableSignal<number> = signal(0);
    private readonly googleMapsApiKey: ResourceRef<string> = resource({
        loader: () => this.configurationService.getString(Config.GOOGLE_MAPS_KEY)
    });

    constructor() {
        afterNextRender(() => this.viewReady.set(true));
    }

    public readonly mapImageSrc: Signal<string> = computed(() => {
        this.retryTrigger(); // Include as dependency to force re-evaluation
        if (!this.viewReady() || !this.googleMapsApiKey.value()) {
            return "";
        }
        const width: number = this.map()?.nativeElement.offsetWidth || 0;
        const height: number = this.map()?.nativeElement.offsetHeight || 0;

        if (width === 0 || height === 0) {
            setTimeout(() => this.retryTrigger.update(v => v + 1), 100);
            return "";
        }

        // noinspection SpellCheckingInspection
        return "https://maps.googleapis.com/maps/api/staticmap" +
            "?size=" + width + "x" + height +
            "&scale=2" +
            "&maptype=roadmap" +
            "&center=" + this.store().latitude + "," + this.store().longitude +
            "&markers=size:mid%7C" + this.store().latitude + "," + this.store().longitude +
            "&zoom=15" +
            "&key=" + this.googleMapsApiKey.value();
    });
}
